import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { RecordAction } from './record-panel.types';
import RecordActionMenuItems from './record-action-menu-items';

// ----------------------------------------------------------------------

/**
 * Shared record-panel action row (JT1-1529). Every action is a labelled button in the same style —
 * no lone icons among labelled buttons (spec item 13). Hidden actions are dropped; the row collapses
 * to nothing when none remain.
 *
 * `moreActions` go under a "More" button, so the panel can offer the record's full action list
 * without a wall of buttons (JT1-1799).
 */
export default function RecordActionBar({ actions, moreActions = [] }: { actions: RecordAction[]; moreActions?: RecordAction[] }) {
  const { t } = useTranslate();
  const morePopover = usePopover();

  const visible = actions.filter((a) => !a.hidden);
  const hasMore = moreActions.some((a) => !a.hidden);
  if (!visible.length && !hasMore) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ px: 2.5, py: 1.5, flexWrap: 'wrap', gap: 1, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
    >
      {visible.map((action) => (
        <Button
          key={action.key}
          size="small"
          variant="soft"
          color={action.color ?? 'inherit'}
          disabled={action.disabled}
          startIcon={<Iconify icon={action.icon} />}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}

      {hasMore && (
        <>
          <Button
            size="small"
            variant="soft"
            color="inherit"
            endIcon={<Iconify icon="eva:chevron-down-fill" />}
            onClick={morePopover.onOpen}
          >
            {t('common.more')}
          </Button>

          <CustomPopover open={morePopover.open} onClose={morePopover.onClose} arrow="top-left" sx={{ minWidth: 200 }}>
            <RecordActionMenuItems actions={moreActions} onClose={morePopover.onClose} />
          </CustomPopover>
        </>
      )}
    </Stack>
  );
}
